import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { createProject, updateProject, getQuotes } from '../services/api';
import { useProjectStore, type Project } from '../stores/projectStore';
import { useCustomerStore } from '../stores/customerStore';
import { Input, Textarea, CustomSelect, LoadingButton, dialogButtonClass, DialogShell, DialogHeader, DialogFooter, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { notifyDataChanged } from '../utils/dataEvents';
import { useTranslation } from 'react-i18next';
import { getTodayDateString } from '../utils/dateUtils';
import { canEditArchivedCustomerData } from '../utils/customerUtils';


interface Quote {
  id: string;
  title: string;
  customer: {
    id: string;
    name: string;
  };
}

interface ProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  project?: Project; // 型別與 projectStore 同一份定義
  preselectedCustomerId?: string;
}

interface FormData {
  name: string;
  customerId: string;
  description?: string;
  status: string;
  startDate: string;
  endDate?: string;
  quoteId?: string;
}

// 報價下拉的上次清單（記憶體，重新整理後清空）：再開對話框時先顯示，背景更新
let quoteOptionsCache: Quote[] | null = null;

const ProjectDialog: React.FC<ProjectDialogProps> = ({
  isOpen,
  onClose,
  project,
  preselectedCustomerId,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm<FormData>();
  const { allCustomers, isLoadingAllCustomers, fetchAllCustomers } = useCustomerStore();
  const [quotes, setQuotes] = useState<Quote[]>(quoteOptionsCache ?? []);
  const [quotesLoading, setQuotesLoading] = useState<boolean>(!quoteOptionsCache);

  const { updateProject: updateProjectInStore, addProject: addProjectToStore, replaceProject, removeProject } = useProjectStore();


  const isEditMode = !!project;
  
  // 檢查是否可以編輯（封存客戶的關聯資料不能編輯）
  const canEdit = !isEditMode || canEditArchivedCustomerData(project?.customer);
  
  // 監聽表單值變化
  const watchedCustomerId = watch('customerId', '');
  const watchedStatus = watch('status', 'preparing');
  const watchedQuoteId = watch('quoteId', '');

  // 載入客戶和報價資料
  useEffect(() => {
    if (isOpen) {
      // 預載入客戶資料
      fetchAllCustomers();
      loadQuotes();
    }
  }, [isOpen, fetchAllCustomers]);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (project) {
        // 編輯模式
        setValue('name', project.name);
        setValue('customerId', project.customerId);
        setValue('description', project.description || '');
        setValue('status', project.status);
        setValue('startDate', project.startDate.split('T')[0]);
        setValue('endDate', project.endDate ? project.endDate.split('T')[0] : '');
        setValue('quoteId', project.quoteId || '');
      } else {
        // 新增模式
        reset();
        if (preselectedCustomerId) {
          setValue('customerId', preselectedCustomerId);
        }
        setValue('status', 'preparing');
        // 設置預設開始日期為今天
        setValue('startDate', getTodayDateString());
      }
    }
  }, [isOpen, project, preselectedCustomerId, setValue, reset]);

  // 報價下拉：有上次的清單就先顯示（不閃「載入中」），背景更新
  const loadQuotes = async () => {
    if (!quoteOptionsCache) setQuotesLoading(true);
    try {
      // 下拉要有全部報價（預設分頁只有 20 筆，第 21 筆以後選不到）
      const quotesResponse = await getQuotes(1, { pageSize: 1000 });
      quoteOptionsCache = quotesResponse.data.data;
      setQuotes(quoteOptionsCache);
    } catch (error) {
      console.error('載入資料失敗:', error);
      if (!quoteOptionsCache) toast.error(t('common.messages.fetchFailed'));
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // 當客戶變更時，檢查目前選擇的報價是否還適用
  const handleCustomerChange = (customerId: string) => {
    setValue('customerId', customerId);
    
    const currentQuoteId = watchedQuoteId;
    if (currentQuoteId) {
      const selectedQuote = quotes.find(quote => quote.id === currentQuoteId);
      if (selectedQuote && selectedQuote.customer?.id !== customerId) {
        // 如果目前選的報價不屬於新選的客戶，清空報價選擇
        setValue('quoteId', '');
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    // 檢查是否可以編輯封存客戶的資料
    if (isEditMode && !canEdit) {
      toast.error(t('errors.business.cannotEditArchivedCustomerData', '無法編輯封存客戶的關聯資料'));
      return;
    }

    const submitData = {
      name: data.name.trim(),
      customerId: data.customerId,
      description: data.description?.trim(),
      status: data.status,
      startDate: data.startDate ? `${data.startDate}T00:00:00.000Z` : undefined,
      endDate: data.endDate ? `${data.endDate}T00:00:00.000Z` : undefined,
      quoteId: data.quoteId || null, // null＝取消關聯（undefined 會被 JSON 略掉，改不回空白）
    };

    const selectedCustomer = allCustomers.find(c => c.id === data.customerId);
    const selectedQuote = quotes.find(q => q.id === data.quoteId);

    if (isEditMode) {
      // UPDATE：保存原始值以供回滾
      const originalProject = { ...project };

      // 樂觀更新 + 立即關閉
      updateProjectInStore(project.id, {
        name: data.name.trim(),
        customerId: data.customerId,
        description: data.description?.trim(),
        status: data.status,
        startDate: data.startDate ? `${data.startDate}T00:00:00.000Z` : project.startDate,
        endDate: data.endDate ? `${data.endDate}T00:00:00.000Z` : undefined,
        quoteId: data.quoteId || null, // null＝取消關聯（undefined 會被 JSON 略掉，改不回空白）
        customer: (selectedCustomer ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          userId: (selectedCustomer as any).userId || (selectedCustomer as any).createdByUserId || '',
          email: selectedCustomer.email,
          company: selectedCustomer.company
        } : project.customer) as any,
        quote: selectedQuote
      });
      handleClose();

      // 背景執行 API 更新
      updateProject(project.id, submitData).then(() => {
        toast.success(t('common.messages.updateSuccess'));
        notifyDataChanged('projects');
      }).catch((error: any) => {
        console.error('專案更新失敗:', error);
        // 回滾到原始值
        updateProjectInStore(project.id, originalProject);
        toast.error(error.response?.data?.error || t('common.messages.updateFailed'));
      });
    } else {
      // CREATE：臨時 id 樂觀新增
      const tempId = `temp-${Date.now()}`;
      const optimisticProject = {
        id: tempId,
        name: data.name.trim(),
        customerId: data.customerId,
        description: data.description?.trim(),
        status: data.status,
        startDate: data.startDate ? `${data.startDate}T00:00:00.000Z` : new Date().toISOString(),
        endDate: data.endDate ? `${data.endDate}T00:00:00.000Z` : undefined,
        quoteId: data.quoteId || null, // null＝取消關聯（undefined 會被 JSON 略掉，改不回空白）
        customer: selectedCustomer ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          userId: (selectedCustomer as any).userId || '',
          archived: selectedCustomer.archived,
        } : { id: data.customerId, name: '', userId: '' },
        quote: selectedQuote,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      addProjectToStore(optimisticProject as any);
      handleClose();

      createProject(submitData).then((response) => {
        if (response?.data) {
          replaceProject(tempId, response.data);
        }
        toast.success(t('common.messages.createSuccess'));
        notifyDataChanged('projects');
      }).catch((error: any) => {
        console.error('專案建立失敗:', error);
        removeProject(tempId);
        toast.error(error.response?.data?.error || t('common.messages.createFailed'));
      });
    }
  };

  if (!isOpen) return null;

  // 篩選當前客戶的報價
  const filteredQuotes = quotes.filter(quote => {
    if (!watchedCustomerId) return true;
    return quote.customer?.id === watchedCustomerId || quote.id === watchedQuoteId;
  });

  return (
    <DialogShell onClose={handleClose} className="max-w-3xl">
      <DialogHeader title={isEditMode ? t('projects.edit.title', { ns: 'pages' }) : t('projects.add.title', { ns: 'pages' })} onClose={handleClose} closeLabel={t('common.actions.close')} />

      {/* 對話框內容 */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          {/* 封存客戶警告提示 */}
          {isEditMode && !canEdit && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('common.warnings.archivedCustomerData')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                    <p>{t('common.warnings.archivedCustomerDataDetail')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <FormFieldWithError
            label={t('projects.form.name', { ns: 'pages' })}
            required={true}
            error={errors.name?.message}
            htmlFor="name"
          >
            <Input
              type="text"
              id="name"
              {...register('name', { 
                required: t('common.forms.validation.required'),
                maxLength: { value: 200, message: t('common.forms.validation.maxLength', { length: 200 }) }
              })}
              variant={errors.name ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('common.forms.labels.customer')}
            required={true}
            error={errors.customerId?.message}
            htmlFor="customerId"
          >
            <CustomSelect
              id="customerId"
              {...register('customerId', { required: t('common.forms.validation.required') })}
              value={watchedCustomerId}
              onChange={handleCustomerChange}
              variant={errors.customerId ? "error" : "default"}
              disabled={isLoadingAllCustomers}
              placeholder={t('common.forms.placeholders.selectCustomer')}
              options={[
                ...(isLoadingAllCustomers ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...allCustomers
                  .filter(customer => customer.archived !== true)
                  .sort((a, b) => new Date(b.createdAt || b.updatedAt || '').getTime() - new Date(a.createdAt || a.updatedAt || '').getTime())
                  .map(customer => ({ 
                    value: customer.id, 
                    label: `${customer.name}${customer.company ? ` (${customer.company})` : ''}` 
                  }))
              ]}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('projects.form.description', { ns: 'pages' })}
            error={errors.description?.message}
            htmlFor="description"
          >
            <Textarea
              id="description"
              {...register('description', {
                maxLength: { value: 1000, message: t('common.forms.validation.maxLength', { length: 1000 }) }
              })}
              rows={4}
              variant={errors.description ? "error" : "default"}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('projects.form.status', { ns: 'pages' })}
            error={errors.status?.message}
            htmlFor="status"
          >
            <CustomSelect
              id="status"
              {...register('status', { required: t('common.forms.validation.required') })}
              value={watchedStatus}
              onChange={(value) => setValue('status', value)}
              variant={errors.status ? "error" : "default"}
              options={[
                { value: 'preparing', label: t('projects.status.preparing', { ns: 'pages' }) },
                { value: 'inProgress', label: t('projects.status.inProgress', { ns: 'pages' }) },
                { value: 'onHold', label: t('projects.status.onHold', { ns: 'pages' }) },
                { value: 'completed', label: t('projects.status.completed', { ns: 'pages' }) },
                { value: 'cancelled', label: t('projects.status.cancelled', { ns: 'pages' }) }
              ]}
            />
          </FormFieldWithError>

          <FormFieldWithError
            label={t('projects.form.quote', { ns: 'pages' })}
            error={errors.quoteId?.message}
            htmlFor="quoteId"
          >
            <CustomSelect
              id="quoteId"
              {...register('quoteId')}
              value={watchedQuoteId}
              onChange={(value) => setValue('quoteId', value)}
              variant={errors.quoteId ? "error" : "default"}
              disabled={quotesLoading}
              placeholder={t('common.forms.placeholders.selectQuote')}
              options={[
                ...(quotesLoading ? [{ value: 'loading', label: t('common.status.loading'), disabled: true }] : []),
                ...(watchedCustomerId && filteredQuotes.length === 0 ? 
                  [{ value: 'no-quotes', label: t('projects.form.noQuotes', { ns: 'pages' }), disabled: true }] : []),
                ...filteredQuotes.map(quote => ({ 
                  value: quote.id, 
                  label: `${quote.title} (${quote.customer?.name})` 
                }))
              ]}
            />
          </FormFieldWithError>

          {/* 開始和結束日期 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormFieldWithError
              label={t('projects.form.startDate', { ns: 'pages' })}
              required={true}
              error={errors.startDate?.message}
              htmlFor="startDate"
            >
              <Input
                type="date"
                id="startDate"
                {...register('startDate', { required: t('common.forms.validation.required') })}
                variant={errors.startDate ? "error" : "default"}
                className="cursor-pointer select-none"
                onMouseDown={(e) => {
                  // 防止文字被選取
                  e.preventDefault();
                }}
                onClick={(e) => {
                  const input = e.target as HTMLInputElement;
                  if (input.type === 'date' && input.showPicker) {
                    try {
                      input.showPicker();
                    } catch (error) {
                      console.log('showPicker not supported or failed');
                    }
                  }
                }}
              />
            </FormFieldWithError>

            <FormFieldWithError
              label={t('projects.form.endDate', { ns: 'pages' })}
              error={errors.endDate?.message}
              htmlFor="endDate"
            >
              <Input
                type="date"
                id="endDate"
                {...register('endDate')}
                variant={errors.endDate ? "error" : "default"}
                className="cursor-pointer select-none"
                onMouseDown={(e) => {
                  // 防止文字被選取
                  e.preventDefault();
                }}
                onClick={(e) => {
                  const input = e.target as HTMLInputElement;
                  if (input.type === 'date' && input.showPicker) {
                    try {
                      input.showPicker();
                    } catch (error) {
                      console.log('showPicker not supported or failed');
                    }
                  }
                }}
              />
            </FormFieldWithError>
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={handleClose}
            className={dialogButtonClass.cancel}
          >
            {t('common.actions.cancel')}
          </button>
          <LoadingButton
            type="submit"
            isEditMode={isEditMode}
            disabled={isLoadingAllCustomers || quotesLoading || !canEdit}
            t={t}
          />
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default ProjectDialog;